import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { ACCEPT, formatBytes } from './stage'
import type { StagedEntry } from './useStagedFiles'

interface Props {
  entries: StagedEntry[]
  onAdd: (files: File[]) => void
  onDrop: (key: string) => void
  onRetry: (key: string) => void
  max: number
  disabled?: boolean
}

/** Drop zone + the picked files, each with its upload to the console. */
export function FileList({ entries, onAdd, onDrop, onRetry, max, disabled = false }: Props) {
  const { t } = useTranslation('bulkMedia')
  const input = useRef<HTMLInputElement | null>(null)
  const [over, setOver] = useState(false)
  const full = entries.length >= max

  return (
    <div className="bm-files">
      <div
        className={`bm-drop ${over ? 'is-over' : ''} ${full || disabled ? 'is-off' : ''}`}
        role="button"
        tabIndex={0}
        aria-label={t('files.drop')}
        onClick={() => !full && !disabled && input.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !full && !disabled) {
            e.preventDefault()
            input.current?.click()
          }
        }}
        onDragOver={(e) => {
          e.preventDefault()
          if (!full && !disabled) setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setOver(false)
          if (!full && !disabled) onAdd([...e.dataTransfer.files])
        }}
      >
        <FeatherIcon name="upload-cloud" size={22} />
        <span className="bm-drop-title">{full ? t('files.full', { max }) : t('files.drop')}</span>
        <span className="bm-muted">{t('files.limits', { max })}</span>
        <input
          ref={input}
          type="file"
          multiple
          accept={ACCEPT}
          hidden
          data-testid="bm-file-input"
          onChange={(e) => {
            onAdd([...(e.target.files ?? [])])
            e.target.value = ''
          }}
        />
      </div>
      {entries.length > 0 && (
        <ul className="bm-filelist" aria-label={t('files.title')}>
          {entries.map((f) => (
            <li key={f.key} className={`bm-file is-${f.status}`}>
              <span className="bm-file-thumb" aria-hidden="true">
                {f.preview ? <img src={f.preview} alt="" /> : <FeatherIcon name={f.kind === 'video' ? 'film' : 'image'} size={16} />}
              </span>
              <span className="bm-file-text">
                <span className="bm-file-name" title={f.name}>
                  {f.name}
                </span>
                <span className="bm-file-meta">
                  {formatBytes(f.size)}
                  {' · '}
                  {f.status === 'failed' ? (
                    <span className="bm-error-text">{f.error || t('files.failed')}</span>
                  ) : (
                    t(`files.status.${f.status}`, { pct: Math.round(f.progress * 100) })
                  )}
                </span>
                {(f.status === 'staging' || f.status === 'queued') && (
                  <span className="bm-bar" role="progressbar" aria-valuenow={Math.round(f.progress * 100)} aria-valuemin={0} aria-valuemax={100}>
                    <span style={{ width: `${f.progress * 100}%` }} />
                  </span>
                )}
              </span>
              {f.status === 'failed' && (
                <button type="button" className="bm-iconbtn" onClick={() => onRetry(f.key)} aria-label={t('files.retry', { name: f.name })} title={t('retry')}>
                  <FeatherIcon name="rotate-cw" size={15} />
                </button>
              )}
              {f.status === 'ready' && <FeatherIcon name="check" size={15} className="bm-ok" />}
              <button type="button" className="bm-iconbtn" onClick={() => onDrop(f.key)} aria-label={t('files.remove', { name: f.name })} title={t('files.removeShort')}>
                <FeatherIcon name="x" size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
